import Cookies from "universal-cookie";
import * as constants from "../../Components/Constant/Constant";
import { mockRequest } from "./Mockrouter";
import { MOCK_PERSONA_IDS } from "./Accountmockpersonas";

export const isDuplicateName = ({
    name,
    items = [],
    nameKey,
    currentName = "",
    isEdit = false,
}) => {
    const normalizedName = name?.trim().toLowerCase();

    if (!normalizedName) return false;

    const normalizedCurrentName = currentName?.trim().toLowerCase();

    return items.some((item) => {
        const itemName = item?.[nameKey]?.trim().toLowerCase();

        if (isEdit && itemName === normalizedCurrentName) {
            return false;
        }

        return itemName === normalizedName;
    });
};

const cookies = new Cookies();

const BASE_URL = constants.BASE_URL_WORKSPACE || "/api";

const MOCKS_ENABLED = process.env.NODE_ENV == false;
// const MOCKS_ENABLED =true;

const MOCK_PERSONA_STORAGE_KEY = "mockPersona";

export const setMockPersona = (personaId) => {
    if (!MOCKS_ENABLED) return;
    if (personaId) {
        window.localStorage.setItem(MOCK_PERSONA_STORAGE_KEY, personaId);
    } else {
        window.localStorage.removeItem(MOCK_PERSONA_STORAGE_KEY);
    }
};

export const getMockPersona = () => {
    if (!MOCKS_ENABLED) return null; // real requests always win in production
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(MOCK_PERSONA_STORAGE_KEY);
};

export { MOCK_PERSONA_IDS, MOCKS_ENABLED };
if (MOCKS_ENABLED && typeof window !== "undefined") {
    const urlPersona = new URLSearchParams(window.location.search).get("mockPersona");
    if (urlPersona) {
        if (MOCK_PERSONA_IDS.includes(urlPersona)) {
            setMockPersona(urlPersona);
        } else {
            console.warn(
                `[mock] Unknown mockPersona "${urlPersona}". Valid ids: ${MOCK_PERSONA_IDS.join(", ")}`
            );
        }
    }
}

async function request(path, { method = "GET", body, headers = {}, signal, userId } = {}) {
    const activePersona = getMockPersona();
    if (activePersona) {
        return mockRequest(activePersona, path, { method, body });
    }

    const effectiveUserId = userId || cookies.get("finuserid");

    const res = await fetch(`${BASE_URL}${path}`, {
        method,
        signal,
        headers: {
            "Content-Type": "application/json",
            ...(effectiveUserId ? { "x-user-id": effectiveUserId } : {}),
            ...headers,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    let data = null;
    const text = await res.text();
    if (text) {
        try {
            data = JSON.parse(text);
        } catch {
            // non-JSON body (e.g. empty 204) - leave data as null
        }
    }

    if (!res.ok) {
        const message =
            (data && (data.message || data.error)) ||
            `Request failed (${res.status} ${res.statusText})`;
        const error = new Error(message);
        error.status = res.status;
        error.data = data;
        throw error;
    }

    return data;
}

export const accountApi = {

    getMe: (signal, userId) => request("/me", { signal, userId }),

    // ---- Plan ----
    getMyPlan: (signal, userId) => request("/me/plan", { signal, userId }),
    renameOrganization: (orgId, name) =>
        request(`/organizations/${orgId}/rename`, { method: "POST", body: { name } }),
    revealApiKey: () => request("/me/api-key/reveal", { method: "POST" }),
    regenerateApiKey: () => request("/me/api-key/regenerate", { method: "POST" }),

    // ---- Workspaces ----
    getOrgWorkspaces: (orgId, signal, userId) =>
        request(`/organizations/${orgId}/workspaces`, { signal, userId }),
    requestAccess: (workspaceId, role = "member") =>
        request("/access-requests", { method: "POST", body: { workspaceId, role } }),
    acceptAccessRequest: (requestId) =>
        request(`/access-requests/${requestId}/accept`, { method: "POST" }),
    declineAccessRequest: (requestId) =>
        request(`/access-requests/${requestId}/decline`, { method: "POST" }),

    // ---- Team / members ----
    getOrgMembers: (orgId, signal) =>
        request(`/organizations/${orgId}/members`, { signal }),
    inviteMember: (orgId, { name, email }) =>
        request(`/organizations/${orgId}/members`, { method: "POST", body: { name, email } }),
    updateMember: (orgId, memberId, payload) =>
        request(`/organizations/${orgId}/members/${memberId}`, { method: "PATCH", body: payload }),
    removeMember: (orgId, memberId) =>
        request(`/organizations/${orgId}/members/${memberId}`, { method: "DELETE" }),

    // ---- Notifications ----
    getNotifications: (signal) => request("/me/notifications", { signal }),
    markNotificationRead: (notificationId) =>
        request(`/me/notifications/${notificationId}/read`, { method: "POST" }),
    getUnreadNotificationCount: (signal) =>
        request("/me/notifications/unread-count", { signal }),

    // ---- Modules (individual) ----
    getMyModules: (signal) => request("/me/modules", { signal }),

    // ---- Billing ----
    getMyInvoices: (signal) => request("/me/invoices", { signal }),
};

export default accountApi;